import { useCallback, useContext, useMemo } from 'react'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import CurrencyContext from 'contexts/currencyContext'
import { generateAccommodationCheckoutItems, generateVillaCheckoutItem } from 'lib/checkout/accommodation/cart'
import { generateAccommodationBundleFlightItems } from 'lib/checkout/flights/cart'
import { isLEHotel } from 'lib/offer/offerTypes'
import type { FlightsClassTypes } from 'constants/flight'
import useCharterFlightJourneysForHotelDates from 'hooks/CharterFlights/useCharterFlightJourneysForHotelDates'

interface Options {
  offer: App.HotelOffer | App.VillaOffer
  packageView?: App.OfferPackageOptionView
  rooms: Array<App.RoomOccupants>
  checkIn?: string
  isFlightsSelected: boolean
  flightOrigin?: App.Airport
  flightPrice?: number
  charterBundleRoute?: App.CharterBundleRoute
  fareClass: FlightsClassTypes
}

function useWeddingGiftRegistryHotelCartItems(options: Options) {
  const {
    offer,
    packageView,
    rooms,
    checkIn,
    isFlightsSelected,
    flightOrigin,
    flightPrice,
    charterBundleRoute,
    fareClass,
  } = options

  const currentCurrency = useContext(CurrencyContext)
  const checkOut = useMemo(() => checkIn && packageView ? moment(checkIn).add(packageView.package.duration, 'days').format(ISO_DATE_FORMAT) : undefined, [checkIn, packageView])

  const {
    departingJourney: departingCharterFlightJourney,
    returningJourney: returningCharterFlightJourney,
    departingFareFamily: departingCharterFareFamily,
    returningFareFamily: returningCharterFareFamily,
    searchResultsTimestamp: charterFlightSearchResultsTimestamp,
    providerSearchId: charterFlightSearchId,
  } = useCharterFlightJourneysForHotelDates({
    charterBundleRoute,
    checkInDate: checkIn,
    checkOutDate: checkOut,
    occupancies: rooms,
    fareClass,
    feederOriginAirportCode: flightOrigin?.code,
  })

  const buildItems = useCallback((): Array<App.Checkout.AnyItem> | undefined => {
    if (!checkIn || !checkOut || !packageView) return undefined

    const dates = { checkIn, checkOut }

    let accommodationItems: Array<App.Checkout.InstantBookingLEHotelItem> = []
    if (isLEHotel(offer)) {
      accommodationItems = generateAccommodationCheckoutItems({
        offer,
        packageOption: packageView,
        dates,
        roomOccupants: rooms,
        currencyCode: currentCurrency,
      })
    }

    let villaItems: Array<App.Checkout.VillaItem> = []
    if (offer.type === 'rental') {
      villaItems = [generateVillaCheckoutItem(offer, packageView, dates, rooms[0], currentCurrency)]
    }

    const shouldAddFlights = (isFlightsSelected || !!charterBundleRoute) && !!flightOrigin
    const flightItems = shouldAddFlights ? generateAccommodationBundleFlightItems({
      rooms,
      airport: flightOrigin,
      offer,
      accommodationItems,
      quotedFare: flightPrice,
      charterBundleRoute,
      fareClass,
      departingCharterFlightJourney,
      returningCharterFlightJourney,
      departingCharterFareFamily,
      returningCharterFareFamily,
      charterFlightSearchResultsTimestamp,
      charterFlightSearchId,
    }) : []

    return [...accommodationItems, ...villaItems, ...flightItems]
  }, [
    charterBundleRoute,
    charterFlightSearchId,
    charterFlightSearchResultsTimestamp,
    checkIn,
    checkOut,
    currentCurrency,
    departingCharterFareFamily,
    departingCharterFlightJourney,
    fareClass,
    flightOrigin,
    flightPrice,
    isFlightsSelected,
    offer,
    packageView,
    returningCharterFareFamily,
    returningCharterFlightJourney,
    rooms,
  ])

  return {
    buildItems,
    canBuildItems: !!checkIn && !!checkOut && !!packageView,
  }
}

export default useWeddingGiftRegistryHotelCartItems
